import { Box, MenuItem, TextField, Typography } from "@mui/material";
import ChipListPicker from "./ChipListPicker";
import { INITIATIVE_FIELDS } from "../../domain/initiativeFields";
import {
  TARGET_PERIOD_PLACEHOLDER,
  isValidTargetPeriod,
} from "../../utils/targetPeriod";
import { useLookups } from "../../hooks/useLookups";

const PROGRESS_MESSAGE_MAX_LENGTH = 500;

export default function InitiativeFormFields({
  draft,
  setField,
  addToField,
  removeFromField,
  originalProgress,
}) {
  const { horizons, statuses, priorities, products, impactAreas } =
    useLookups();
  const horizonNames = horizons.map((h) => h.name);
  const statusNames = statuses.map((s) => s.name);
  const priorityNames = priorities.map((p) => p.name);
  const productNames = products.map((p) => p.name);
  const impactAreaNames = impactAreas.map((a) => a.name);

  const hasDuplicateLines = (lines) => {
    const seen = new Set();
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (seen.has(trimmed)) return "Duplicate lines will be removed on save";
      seen.add(trimmed);
    }
    return false;
  };

  const clampToMin = (rawValue, min, max = Infinity) => {
    if (rawValue === "") return "";
    const n = Number(rawValue);
    if (!Number.isFinite(n)) return "";
    return Math.min(max, Math.max(min, n));
  };

  const handleProgressChange = (rawValue) => {
    const clamped = clampToMin(
      rawValue,
      INITIATIVE_FIELDS.progress.min,
      INITIATIVE_FIELDS.progress.max,
    );
    setField("progress", clamped);
    if (clamped === originalProgress) setField("progressMessage", "");
  };

  const progressChanged =
    draft.progress !== "" && draft.progress !== originalProgress;

  const selectionFields = [
    { field: "horizon", label: "Horizon", options: horizonNames },
    { field: "status", label: "Status", options: statusNames },
    { field: "priority", label: "Priority", options: priorityNames },
  ];

  const styles = {
    sectionLabel: { mb: 1 },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 2,
      mb: 2,
    },
    selectMenu: {
      slotProps: { paper: { style: { maxHeight: 320, maxWidth: 360 } } },
    },
    menuItemText: {
      display: "block",
      maxWidth: 200,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    linksHint: { textTransform: "none", fontWeight: 400 },
    progressNote: { mb: 2 },
  };

  return (
    <>
      <ChipListPicker
        label="Product *"
        addLabel="product"
        values={draft.product}
        options={productNames}
        onAdd={(value) => addToField("product", value)}
        onRemove={(value) => removeFromField("product", value)}
      />

      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={styles.sectionLabel}
      >
        Classification &amp; Timeline
      </Typography>
      <Box sx={styles.grid}>
        <TextField
          fullWidth
          required
          size="small"
          label="Owner"
          value={draft.owner}
          onChange={(e) => setField("owner", e.target.value)}
          helperText={
            draft.owner.length >= INITIATIVE_FIELDS.owner.maxLength
              ? `Character limit reached (${INITIATIVE_FIELDS.owner.maxLength})`
              : undefined
          }
          slotProps={{
            htmlInput: { maxLength: INITIATIVE_FIELDS.owner.maxLength },
          }}
        />
        {selectionFields.map(({ field, label, options }) => (
          <TextField
            key={field}
            select
            fullWidth
            required
            size="small"
            label={label}
            value={draft[field]}
            onChange={(e) => setField(field, e.target.value)}
            slotProps={{ select: { MenuProps: styles.selectMenu } }}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                <Box component="span" sx={styles.menuItemText}>
                  {option}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        ))}
        <TextField
          fullWidth
          size="small"
          label="Target Period"
          placeholder={TARGET_PERIOD_PLACEHOLDER}
          value={draft.targetPeriod || ""}
          onChange={(e) => setField("targetPeriod", e.target.value)}
          error={!isValidTargetPeriod(draft.targetPeriod)}
          helperText={
            !isValidTargetPeriod(draft.targetPeriod)
              ? `Format: ${TARGET_PERIOD_PLACEHOLDER}`
              : undefined
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          required
          size="small"
          type="number"
          label="Estimate (quarters)"
          slotProps={{
            htmlInput: {
              min: INITIATIVE_FIELDS.estimate.min,
              max: INITIATIVE_FIELDS.estimate.max,
            },
          }}
          value={draft.estimate}
          onChange={(e) =>
            setField(
              "estimate",
              clampToMin(
                e.target.value,
                INITIATIVE_FIELDS.estimate.min,
                INITIATIVE_FIELDS.estimate.max,
              ),
            )
          }
        />
        <TextField
          fullWidth
          required
          size="small"
          type="number"
          label="Progress (%)"
          slotProps={{
            htmlInput: {
              min: INITIATIVE_FIELDS.progress.min,
              max: INITIATIVE_FIELDS.progress.max,
            },
          }}
          value={draft.progress}
          onChange={(e) => handleProgressChange(e.target.value)}
        />
      </Box>

      <TextField
        fullWidth
        multiline
        size="small"
        label="Progress Note"
        placeholder="What changed?"
        disabled={!progressChanged}
        value={draft.progressMessage || ""}
        onChange={(e) => setField("progressMessage", e.target.value)}
        helperText={
          (draft.progressMessage || "").length >= PROGRESS_MESSAGE_MAX_LENGTH
            ? `Character limit reached (${PROGRESS_MESSAGE_MAX_LENGTH})`
            : undefined
        }
        slotProps={{
          htmlInput: { maxLength: PROGRESS_MESSAGE_MAX_LENGTH },
        }}
        sx={styles.progressNote}
      />

      <ChipListPicker
        label="Impact Areas"
        addLabel="impact area"
        values={draft.impactAreas}
        options={impactAreaNames}
        onAdd={(value) => addToField("impactAreas", value)}
        onRemove={(value) => removeFromField("impactAreas", value)}
      />
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={styles.sectionLabel}
      >
        Links
        <Box component="span" sx={styles.linksHint}>
          {" "}
          — separate multiple with a new line
        </Box>
      </Typography>
      <TextField
        fullWidth
        size="small"
        multiline
        label="GitLab Epic"
        value={draft.gitlabEpic.join("\n")}
        onChange={(e) => setField("gitlabEpic", e.target.value.split("\n"))}
        helperText={hasDuplicateLines(draft.gitlabEpic)}
      />
      <TextField
        fullWidth
        size="small"
        multiline
        label="Design Link"
        value={draft.designLink.join("\n")}
        onChange={(e) => setField("designLink", e.target.value.split("\n"))}
        helperText={hasDuplicateLines(draft.designLink)}
      />
    </>
  );
}
